import React, { useState, useEffect } from "react";
import axios from "axios";
import "../Page.css";
import "./Analytics.css";
import { security_key, Url } from "../../Services/Api";
import DataTable from "react-data-table-component";
import AnalyticsProductCard from "../../Components/AnalyticsComponents/OverviewAnalytics/AnalyticsProductCard";
import ShimmerLoader from "../../Components/UI-Controls/Loader/ShimmerLoader";
import { FaHandHoldingUsd, FaCubes, FaClipboardList } from "react-icons/fa";
import ProductVisualize from "../../Components/AnalyticsComponents/OverviewAnalytics/ProductVisualization";
import Pagination from "../../Components/UI-Controls/Pagination/PaginationRashid";
import { DatePicker } from "antd";
import BottomToust from "../../Components/BottomToust/BottomToust";

const OrderAnalytics = () => {
  const { RangePicker } = DatePicker;
  const rowsPerPage = 7;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [productOrders, setProductOrders] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState("totalOrder");
  const [metricName, setMetricName] = useState("Total Orders");
  const [componentName, setComponentName] = useState("Orders Analytics");
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalNetSales, setTotalNetSales] = useState(0);
  const [totalItemsSold, setTotalItemsSold] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [dateRange, setDateRange] = useState(null);
  const [chartData, setChartData] = useState({
    seriesData: [],
    categories: [],
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [showMessage, setShowMessage] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const handleDateRange = (dates) => {
    setDateRange(dates);
    if (dates && dates.length === 2) {
      const startDate = dates[0].format("YYYY-MM-DD");
      const endDate = dates[1].format("YYYY-MM-DD");
      fetchProductData(startDate, endDate);
    }
  };

  const handleCloseMessageModal = () => {
    setShowMessage(false);
  };

  useEffect(() => {
    if (showMessage === true) {
      const timeOut = setTimeout(() => {
        setShowMessage(false);
      }, 3000);

      return () => clearTimeout(timeOut);
    }
  }, [showMessage]);

  const [filtersApplied, setFiltersApplied] = useState(false);

  const fetchProductData = async (startDate, endDate) => {
    setLoading(true);
    setError(null);
    const token = sessionStorage.getItem('authToken');
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      'x-api-key': security_key
    };

    try {
      const response = await axios.get(`${Url}/api/v1/analytics/order-analytics`, {
        params: { startDate, endDate },
        headers: headers
      });

      if (!response.data) throw new Error("Invalid API response format");

      const result = response.data;
      setData(result);

      const productsArray = result?.table

      const totalOrders = productsArray.reduce(
        (sum, product) => sum + (product.orders || 0),
        0
      );
      const totalNetSales = productsArray.reduce(
        (sum, product) => sum + (product.netSale || 0),
        0
      );
      const totalItemsSold = productsArray.reduce(
        (sum, product) => sum + (product.quantity || 0),
        0
      );
      const totalProducts = productsArray.length;

      setProductOrders(productsArray);
      setTotalOrders(totalOrders);
      setTotalNetSales(totalNetSales);
      setTotalItemsSold(totalItemsSold);
      setTotalProducts(totalProducts);

      /* --- Date Wise Handling --- */

      if (!Array.isArray(result.graph))
        throw new Error("Invalid or missing sale_chart data");

      const sortedSaleChart = [...result.graph].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );

      const aggregatedSaleData = sortedSaleChart.reduce((acc, entry) => {
        const dateKey = entry.date;
        acc[dateKey] = (acc[dateKey] || 0) + Number(entry[selectedMetric] || 0);
        return acc;
      }, {});

      const sortedDates = Object.keys(aggregatedSaleData).sort(
        (a, b) => new Date(a) - new Date(b)
      );

      setChartData({
        seriesData: sortedDates.map((date) => aggregatedSaleData[date]),
        labels: sortedDates.map((date) =>
          new Date(date).toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
          })
        ),
      });

      setToastMessage("Order Analytics Data fetched successfully!");
      setShowMessage(true);

    } catch (error) {
      const errMsg = error?.response?.data?.message || error.message;
      setError(errMsg);
      setToastMessage(`Failed to fetch Order Analytics: ${errMsg}`);
      setShowMessage(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMetric) {
      fetchProductData();
    }
  }, [selectedMetric]);

  /* --- Click Handlers for Cards --- */
  const handleMetricChange = (metric, name) => {
    setSelectedMetric(metric);
    setComponentName("Orders Analytics");
    setMetricName(name);
  };

  /* --- Function for representing numbers & currency in standard way --- */
  const formatNumber = (num) => {
    if (!num) return "N/A";
    const formattedNum = parseFloat(num).toFixed(2);
    return new Intl.NumberFormat("en-US").format(formattedNum);
  };

  const formatCurrency = (amount) => {
    if (!amount) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  /* --- Overview Header Cards Handling --- */
  const ProductsAnalyticsCardData = [
    {
      title: "Total Orders",
      duration: "Last 7 Days",
      value: loading ? (
        <ShimmerLoader width="100px" height="22.5px" borderRadius="20px" />
      ) : (
        formatNumber(data?.summary?.totalOrders || 0)
      ),
      growth: 20.05,
      icon: <FaClipboardList />,
      onClick: () => handleMetricChange("totalOrder", "Total Orders"),
    },
    {
      title: "Net Sale",
      duration: "Last 7 Days",
      value: loading ? (
        <ShimmerLoader width="100px" height="22.5px" borderRadius="20px" />
      ) : (
        formatNumber(data?.summary?.netSale || 0)
      ),
      growth: 15.78,
      icon: <FaCubes />,
      onClick: () => handleMetricChange("itemsSold", "Total Items Sold"),
    },
    {
      title: "Average Order Value",
      duration: "Last 7 Days",
      value: loading ? (
        <ShimmerLoader width="100px" height="22.5px" borderRadius="20px" />
      ) : (
        formatCurrency(data?.summary?.averageOrderValue || 0)
      ),
      growth: 25.92,
      icon: <FaHandHoldingUsd />,
      onClick: () => handleMetricChange("netSale", "Net Sales"),
    },
    {
      title: "Average Items/Order",
      duration: "Last 7 Days",
      value: loading ? (
        <ShimmerLoader width="100px" height="22.5px" borderRadius="20px" />
      ) : (
        data?.summary?.averageItemsPerOrder
      ),
      growth: 25.92,
      icon: <FaHandHoldingUsd />,
      onClick: () => { },
    },
  ];

  /* --- Data Table Handling --- */

  const customStyles = {
    headCells: {
      style: {
        height: "52px",
        background: "transparent",
        opacity: "1",
        textAlign: "center",
        justifyContent: "center",
        border: "none",
        color: "var(--text-color-1)",
        fontFamily: "var(--font-family)",
        fontWeight: "var(--font-weight-medium)",
        fontSize: "var(--font-size-medium)",
      },
    },
    cells: {
      style: {
        height: "66px",
        justifyContent: "center",
        textAlign: "center",
        background: "transparent",
        borderTop: "var(--standered-border)",
        borderRight: "none",
        color: "var(--text-color-1)",
        fontFamily: "var(--font-family)",
        fontWeight: "var(--font-weight-regular)",
        fontSize: "var(--font-size-small)",
      },
    },
  };

  const ProductsDataColumns = [
    {
      name: "Date",
      selector: (row) => row.date,
      minWidth: "150px",
      maxWidth: "190px",
      cell: (row) =>
        loading ? (
          <ShimmerLoader width="110px" height="22.5px" borderRadius="20px" />
        ) : row.date ? (
          new Date(row.date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        ) : (
          "N/A"
        ),
    },
    {
      name: "Order#",
      selector: (row) =>
        loading ? (
          <ShimmerLoader width="65px" height="22.5px" borderRadius="20px" />
        ) : (
          "ORDER-" + row.uid || "N/A"
        ),
      minWidth: "80px",
      maxWidth: "120px",
    },
    {
      name: "Status",
      wrap: true,
      cell: (row) => {
        if (loading) {
          return (
            <ShimmerLoader width="90px" height="22.5px" borderRadius="20px" />
          );
        }
        let textColor;
        let backgroundColor;
        let stockLabel;
        switch (row.status) {
          case "processing":
            textColor = "#3A7D99";
            backgroundColor = "#3A7D9910";
            stockLabel = "Processing";
            break;
          case "pending":
            textColor = "#D6B656";
            backgroundColor = "#D6B65610";
            stockLabel = "Pending";
            break;
          case "cancelled":
            textColor = "#B45A67";
            backgroundColor = "#B45A6710";
            stockLabel = "Canceled";
            break;
          case "failed":
            textColor = "#A33D4A";
            backgroundColor = "#A33D4A10";
            stockLabel = "Failed";
            break;
          case "pending":
            textColor = "#F4B074";
            backgroundColor = "#F4B07410";
            stockLabel = "Pending";
            break;
          case "on-hold":
            textColor = "#6C757D";
            backgroundColor = "#6C757D10";
            stockLabel = "On Hold";
            break;
          case "refunded":
            textColor = "#835D97";
            backgroundColor = "#835D9720";
            stockLabel = "Refunded";
            break;
          case "processing":
            textColor = "#F4B074";
            backgroundColor = "#F4B07410";
            stockLabel = "Processing";
            break;
          case "delivered":
            textColor = "#4F8057";
            backgroundColor = "#4F805710";
            stockLabel = "Delivered";
            break;
          case "completed":
            textColor = "#4F8057";
            backgroundColor = "#4F805710";
            stockLabel = "Completed";
            break;
          default:
            textColor = "#999";
            backgroundColor = "#E0E0E010";
            stockLabel = "Unknown";
        }
        return (
          <div
            style={{
              color: textColor,
              backgroundColor: backgroundColor,
              padding: "5px 10px",
              borderRadius: "5px",
              display: "inline-block",
              fontWeight: 600,
              whiteSpace: "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
              maxWidth: "100%",
            }}
          >
            {stockLabel}
          </div>
        );
      },
      minWidth: "100px",
      maxWidth: "140px",
    },
    {
      name: "Type",
      selector: (row) => row.netSale,
      minWidth: "100px",
      maxWidth: "140px",
      cell: (row) =>
        loading ? (
          <ShimmerLoader width="90px" height="22.5px" borderRadius="20px" />
        ):(
          "--"
        ),
    },
    {
      name: "Customer",
      cell: (row) =>
        loading ? (
          <ShimmerLoader width="200px" height="22.5px" borderRadius="20px" />
        ) : (
          <div>
            <div>
              {row?.customer}
            </div>{" "}
            <div style={{ fontSize: "0.9em", color: "#666" }}>
              {row?.customerMail}
            </div>{" "}
          </div>
        ),
      minWidth: "280px",
      maxWidth: "350px",
    },
     {
      name: "Items Sold",
      selector: (row) => row.netSale,
      minWidth: "100px",
      maxWidth: "140px",
      cell: (row) =>
        loading ? (
          <ShimmerLoader width="90px" height="22.5px" borderRadius="20px" />
        ) : row.products ? (
          `${row.products?.length}`
        ) : (
          "N/A"
        ),
    },
    {
      name: "Net Sales",
      selector: (row) => row.netSale,
      minWidth: "125px",
      maxWidth: "175px",
      cell: (row) =>
        loading ? (
          <ShimmerLoader width="90px" height="22.5px" borderRadius="20px" />
        ) : row.netSale !== undefined && row.netSale !== null ? (
          `$${formatNumber(row.netSale)}`
        ) : (
          "N/A"
        ),
    }
  ];

  const totalPages = Math.ceil(
    (Array.isArray(productOrders) ? productOrders.length : 0) / rowsPerPage
  );

  const paginatedData = Array.isArray(productOrders)
    ? productOrders.slice(
      (currentPage - 1) * rowsPerPage,
      currentPage * rowsPerPage
    )
    : [];

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const placeholderRows = Array(7).fill({ uid: "", name: "", image: "" });

  const displayedData = loading ? placeholderRows : paginatedData;

  const disabledDate = (current) => {
    return current && current > new Date();
  };

  return (
    <div className="ProductsAnalytics-Layout">

      {/* --- Title & Filteration Row --- */}
      <div className="ProductsAnalytics-Row01">
        <div className="RevenueAnalytics-1stRow">
          <div className="RevenueAnalytics-Title">Orders</div>
          <div className="RevenueAnalytics-Filter">
            <RangePicker
              format="YYYY-MM-DD"
              onChange={handleDateRange}
              value={dateRange}
              style={{ width: "100%" }}
              disabledDate={disabledDate}
            />
          </div>
        </div>
      </div>

      {/*--- Orders Analytics Card Section ---*/}
      <div className="ProductsAnalytics-Row01">
        {/* --- Orders Header Cards Section --- */}
        {Array.isArray(ProductsAnalyticsCardData) &&
          ProductsAnalyticsCardData.length > 0 ? (
          ProductsAnalyticsCardData.map((item, index) => (
            <div
              key={index}
              className="Products-Row01-Card"
              onClick={item.onClick}
            >
              <AnalyticsProductCard
                title={item.title}
                duration={item.duration}
                value={item.value}
                growth={item.growth}
                icon={item.icon}
              />
            </div>
          ))
        ) : (
          <p>Loading data...</p>
        )}
      </div>

      {/*--- Visualization Chart  Section ---*/}
      <div className="ProductsAnalytics-Row01">
        <ProductVisualize
          SalesData={{
            labels: chartData?.labels || [],
            seriesData: Array.isArray(chartData?.seriesData)
              ? chartData.seriesData
              : [],
          }}
          componentName={componentName}
          metricName={metricName}
          loading={loading}
        />
      </div>

      {/* --- Orders API Data Table Section --- */}
      <div className="ProductsAnalytics-Row01">
        <div className="Orders-Table-Data">
          <div>
            <DataTable
              columns={ProductsDataColumns}
              data={displayedData}
              customStyles={customStyles}
            />
            <Pagination
              activePageIndex={currentPage}
              totalPages={totalPages}
              onPrevPage={handlePrevPage}
              onNextPage={handleNextPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>

      {/* --- Data Table Calculation Row --- */}
      <div className="ProductsAnalytics-Row01">
        <div className="ProductAnalytics-LastRow">
          <div className="LastRow-ProductCount">
            <div className="LastRow-Value">
              {loading ? (
                <ShimmerLoader
                  width="80px"
                  height="22.5px"
                  borderRadius="10px"
                />
              ) : (
                formatNumber(totalProducts)
              )}
            </div>
            <span>Products</span>
          </div>
          <div className="LastRow-ItemSold">
            <div className="LastRow-Value">
              {loading ? (
                <ShimmerLoader
                  width="80px"
                  height="22.5px"
                  borderRadius="10px"
                />
              ) : (
                formatNumber(totalItemsSold)
              )}
            </div>
            <span>Items Sold</span>
          </div>
          <div className="LastRow-NetSales">
            <div className="LastRow-Value">
              {loading ? (
                <ShimmerLoader
                  width="100px"
                  height="22.5px"
                  borderRadius="10px"
                />
              ) : (
                formatCurrency(totalNetSales)
              )}
            </div>
            <span>Net Sales</span>
          </div>
          <div className="LastRow-TotalOrder">
            <div className="LastRow-Value">
              {loading ? (
                <ShimmerLoader
                  width="80px"
                  height="22.5px"
                  borderRadius="10px"
                />
              ) : (
                formatNumber(totalOrders)
              )}
            </div>
            <span>Orders</span>
          </div>
        </div>
      </div>

      <BottomToust
        showMessage={showMessage}
        message={toastMessage}
        handleCloseMessageModal={() => handleCloseMessageModal()}
      />

    </div>
  );
};

export default OrderAnalytics;